import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fmtPHP, fmtDate, fmtDateTime, LOAN_STATUSES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { LoanAdminTools } from "@/components/admin/LoanAdminTools";
import { ArrowLeft, CreditCard, Loader2, Download, Printer } from "lucide-react";
import { toast } from "sonner";

const METHODS = ["GCash", "Maya", "Bank Transfer", "OTC Cash", "Other"];

function PaymentDialog({ loan, open, onOpenChange, onDone }) {
  const [amount, setAmount] = useState(loan.monthly_payment);
  const [method, setMethod] = useState("GCash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      const { data } = await api.post("/payments", {
        loan_id: loan.id,
        amount: parseFloat(amount),
        method,
        reference: reference || null,
        notes: notes || null,
      });
      toast.success("Payment recorded", { description: `Receipt ${data.receipt_no}` });
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#101624] border border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Record payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-slate-400">Amount (₱)</Label>
            <Input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payment-amount-input"
              className="bg-black/30 border-white/10 text-white font-mono"
            />
            <div className="flex gap-2 mt-2">
              {[loan.monthly_payment, loan.monthly_payment * 2, loan.outstanding_balance].map(
                (v, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setAmount(v)}
                    data-testid={`payment-preset-${i}`}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-white/5 hover:bg-emerald-500/15 text-slate-300 hover:text-emerald-300 border border-white/5"
                  >
                    {i === 2 ? "Full outstanding" : i === 1 ? "2× monthly" : "1× monthly"} ·{" "}
                    {fmtPHP(v)}
                  </button>
                )
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger
                data-testid="payment-method-select"
                className="bg-black/30 border-white/10 text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#101624] border-white/10 text-white">
                {METHODS.map((m) => (
                  <SelectItem key={m} value={m} className="focus:bg-emerald-500/15">
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Reference number</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="GCash / Bank txn id"
              data-testid="payment-reference-input"
              className="bg-black/30 border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="payment-notes-input"
              className="bg-black/30 border-white/10 text-white min-h-[60px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="save-payment-button"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LoanDetail() {
  const { id } = useParams();
  const [loan, setLoan] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get(`/loans/${id}`);
    setLoan(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/loans/${id}/status`, { status });
      toast.success(`Status set to ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const printReceipt = (p) => {
    const html = `
      <html><head><title>Receipt ${p.receipt_no}</title>
      <style>body{font-family:system-ui;padding:40px;max-width:600px;margin:auto}
      h1{font-size:20px;margin:0}table{width:100%;border-collapse:collapse;margin-top:20px}
      td{padding:8px 0;border-bottom:1px solid #eee}.muted{color:#666;font-size:12px}
      .total{font-size:24px;font-weight:bold;color:#059669}</style></head>
      <body>
      <h1>LendingPH · Payment Receipt</h1>
      <p class="muted">Receipt No. <b>${p.receipt_no}</b> · ${new Date(
      p.paid_at
    ).toLocaleString("en-PH")}</p>
      <table>
        <tr><td>Borrower</td><td style="text-align:right"><b>${p.borrower_name}</b></td></tr>
        <tr><td>Loan ID</td><td style="text-align:right"><code>${p.loan_id.slice(0, 8)}</code></td></tr>
        <tr><td>Method</td><td style="text-align:right">${p.method}</td></tr>
        <tr><td>Reference</td><td style="text-align:right">${p.reference || "—"}</td></tr>
        <tr><td>Notes</td><td style="text-align:right">${p.notes || "—"}</td></tr>
        <tr><td style="padding-top:20px">Amount paid</td><td style="padding-top:20px;text-align:right" class="total">₱${p.amount.toLocaleString(
          "en-PH",
          { minimumFractionDigits: 2 }
        )}</td></tr>
      </table>
      <p class="muted" style="margin-top:30px">Thank you for your payment. For questions, contact LendingPH support.</p>
      </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  if (!loan) {
    return (
      <div className="py-20 text-center text-slate-400 font-mono" data-testid="loan-loading">
        Loading loan…
      </div>
    );
  }

  const progress = Math.min(100, (loan.paid_amount / loan.total_repayment) * 100);

  return (
    <div className="space-y-6" data-testid={`loan-detail-${loan.id}`}>
      <button
        onClick={() => navigate(-1)}
        data-testid="loan-back-button"
        className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-emerald-300"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
            Loan Record
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-1">
            {loan.borrower_name}
          </h1>
          <div className="text-slate-400 text-sm mt-1 font-mono">
            {loan.id.slice(0, 8).toUpperCase()} · Opened {fmtDate(loan.start_date)} · Matures{" "}
            {fmtDate(loan.maturity_date)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={loan.status} />
          <Select value={loan.status} onValueChange={changeStatus}>
            <SelectTrigger
              data-testid="loan-status-change-select"
              className="w-44 bg-black/30 border-white/10 text-white h-9 text-xs"
            >
              <SelectValue placeholder="Change status" />
            </SelectTrigger>
            <SelectContent className="bg-[#101624] border-white/10 text-white">
              {LOAN_STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="focus:bg-emerald-500/15">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => setOpen(true)}
            data-testid="record-payment-button"
            disabled={loan.outstanding_balance <= 0.01}
            className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Record payment
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-sky-500/5 border border-sky-500/20 p-4">
        <Link to={`/loans/${loan.id}/review`} data-testid="loan-open-review" className="text-sm font-semibold text-sky-300 hover:text-sky-200">Review application →</Link>
        <Link to={`/borrowers/${loan.borrower_id}?loan=${loan.id}`} data-testid="loan-open-checking-data" className="text-sm text-slate-300 hover:text-sky-300">Checking Data</Link>
      </div>
      <LoanAdminTools loan={loan} onDone={load} />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Principal", value: fmtPHP(loan.principal), tint: "text-slate-200" },
          { label: "Monthly payment", value: fmtPHP(loan.monthly_payment), tint: "text-white" },
          { label: "Total interest", value: fmtPHP(loan.total_interest), tint: "text-amber-300" },
          { label: "Total repayment", value: fmtPHP(loan.total_repayment), tint: "text-slate-200" },
          { label: "Paid to date", value: fmtPHP(loan.paid_amount), tint: "text-cyan-300" },
          {
            label: "Outstanding",
            value: fmtPHP(loan.outstanding_balance),
            tint: "text-emerald-300",
          },
          { label: "Term", value: `${loan.term_months} months`, tint: "text-slate-200" },
          { label: "Rate", value: "0.3% / mo flat", tint: "text-slate-200" },
        ].map((m) => (
          <Card key={m.label} className="card-glow p-4 rounded-xl bg-[#111826] border border-white/5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              {m.label}
            </div>
            <div className={`font-mono tabular text-xl font-bold mt-1 ${m.tint}`}>{m.value}</div>
          </Card>
        ))}
      </section>

      <Card className="card-glow p-5 rounded-xl bg-[#111826] border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-white">Repayment progress</h2>
          <span className="font-mono tabular text-sm text-emerald-300">{progress.toFixed(1)}%</span>
        </div>
        <div className="h-2.5 bg-white/5 rounded-full overflow-hidden border border-white/5">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
            style={{ width: `${progress}%` }}
            data-testid="loan-progress-bar"
          />
        </div>
      </Card>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="card-glow rounded-xl bg-[#111826] border border-white/5 overflow-hidden">
          <div className="p-5 border-b border-white/5">
            <h2 className="font-display text-lg font-semibold text-white">Repayment schedule</h2>
            <p className="text-xs text-slate-500 mt-1">
              {loan.installments?.length || 0} installments · Auto-updates as payments are recorded
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs uppercase text-slate-400">#</TableHead>
                <TableHead className="text-xs uppercase text-slate-400">Due date</TableHead>
                <TableHead className="text-xs uppercase text-slate-400 text-right">Due</TableHead>
                <TableHead className="text-xs uppercase text-slate-400 text-right">Paid</TableHead>
                <TableHead className="text-xs uppercase text-slate-400">State</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loan.installments?.map((i) => (
                <TableRow
                  key={i.id}
                  data-testid={`installment-row-${i.installment_number}`}
                  className="border-white/5"
                >
                  <TableCell className="font-mono text-slate-400">{i.installment_number}</TableCell>
                  <TableCell className="text-slate-200">{fmtDate(i.due_date)}</TableCell>
                  <TableCell className="font-mono tabular text-right text-slate-200">
                    {fmtPHP(i.amount_due)}
                  </TableCell>
                  <TableCell className="font-mono tabular text-right text-emerald-300">
                    {fmtPHP(i.amount_paid)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={i.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="card-glow rounded-xl bg-[#111826] border border-white/5 overflow-hidden">
          <div className="p-5 border-b border-white/5">
            <h2 className="font-display text-lg font-semibold text-white">Payment history</h2>
            <p className="text-xs text-slate-500 mt-1">
              {loan.payments?.length || 0} recorded payments
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs uppercase text-slate-400">Receipt</TableHead>
                <TableHead className="text-xs uppercase text-slate-400">Date</TableHead>
                <TableHead className="text-xs uppercase text-slate-400 text-right">
                  Amount
                </TableHead>
                <TableHead className="text-xs uppercase text-slate-400">Method</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(loan.payments?.length || 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    No payments yet.
                  </TableCell>
                </TableRow>
              )}
              {loan.payments?.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`} className="border-white/5">
                  <TableCell className="font-mono text-xs text-slate-300">{p.receipt_no}</TableCell>
                  <TableCell className="text-slate-300 text-sm">{fmtDateTime(p.paid_at)}</TableCell>
                  <TableCell className="font-mono tabular text-right text-emerald-300 font-semibold">
                    {fmtPHP(p.amount)}
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">{p.method}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => printReceipt(p)}
                      data-testid={`download-receipt-button-${p.id}`}
                      className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                    >
                      <Printer className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </section>

      <PaymentDialog loan={loan} open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}
